import { useContext, useState } from "react";
import { Check, Pencil, Plus, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { EmployeeAssignment } from "@/components/EmployeeAssignment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NAME_MAX_LENGTH = 60;

/**
 * Grupos de asesores — named presets that fill the Tablero's Asesor filter in
 * one click. Self-contained on purpose: HandoffManagement already drills ~25
 * callbacks, so this tab owns its own queries and mutations instead of adding
 * more. `employees.list` is already cached by AssignmentContainer, so the
 * roster costs nothing extra.
 *
 * Membership is a group being a set of employee ids — never a permission. See
 * thoughts/research/2026-08-28-employee-groups-tablero-filter.md.
 */
export const GruposAsesoresTab = () => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const groups = trpc.employeeGroups.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );
  const employees = trpc.employees.list.useQuery({ merchantId }, { enabled: !!merchantId });

  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [groupToDelete, setGroupToDelete] = useState<{ id: string; name: string } | null>(null);

  const invalidate = () => utils.employeeGroups.list.invalidate({ merchantId });
  const handleError = (fallback: string) => (error: { message?: string }) =>
    toast.error(error.message || fallback);

  const createGroup = trpc.employeeGroups.create.useMutation({
    onSuccess: () => {
      invalidate();
      setNewName("");
      setIsCreating(false);
      toast.success("Grupo creado");
    },
    onError: handleError("No se pudo crear el grupo"),
  });

  const renameGroup = trpc.employeeGroups.update.useMutation({
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      toast.success("Grupo actualizado");
    },
    onError: handleError("No se pudo actualizar el grupo"),
  });

  const addEmployees = trpc.employeeGroups.addEmployees.useMutation({
    onSuccess: invalidate,
    onError: handleError("No se pudieron agregar los asesores"),
  });

  const removeEmployees = trpc.employeeGroups.removeEmployees.useMutation({
    onSuccess: invalidate,
    onError: handleError("No se pudieron quitar los asesores"),
  });

  const deleteGroup = trpc.employeeGroups.remove.useMutation({
    onSuccess: () => {
      invalidate();
      setGroupToDelete(null);
      toast.success("Grupo eliminado");
    },
    onError: handleError("No se pudo eliminar el grupo"),
  });

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    createGroup.mutate({ merchantId, name });
  };

  const handleRename = (id: string) => {
    const name = editingName.trim();
    if (!name) return;
    renameGroup.mutate({ merchantId, id, name });
  };

  const handleStartEditing = (group: { id: string; name: string }) => {
    setEditingId(group.id);
    setEditingName(group.name);
  };

  if (groups.isLoading || employees.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">Cargando grupos...</p>
        </div>
      </div>
    );
  }

  const availableEmployees = employees.data ?? [];
  const groupList = groups.data ?? [];

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="flex flex-col gap-1">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Un grupo junta varios asesores bajo un nombre. En el Tablero podés filtrar por el
          grupo y se seleccionan todos sus asesores de una vez.
        </p>
      </div>

      {isCreating ? (
        <Card className="flex flex-col gap-3 p-4">
          <label htmlFor="nuevo-grupo" className="text-sm font-medium text-foreground">
            Nombre del grupo
          </label>
          <Input
            id="nuevo-grupo"
            autoFocus
            value={newName}
            maxLength={NAME_MAX_LENGTH}
            placeholder="Ej: Ventas Norte"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
              if (e.key === "Escape") setIsCreating(false);
            }}
          />
          <p className="text-xs text-muted-foreground">
            Después de crearlo vas a poder agregarle asesores.
          </p>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!newName.trim() || createGroup.isLoading}>
              Crear grupo
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreating(false);
                setNewName("");
              }}
            >
              Cancelar
            </Button>
          </div>
        </Card>
      ) : (
        <div>
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 size-4" />
            Nuevo grupo
          </Button>
        </div>
      )}

      {groupList.length === 0 && !isCreating ? (
        <Card className="flex flex-col items-center gap-3 p-8 text-center">
          <Users className="size-8 text-muted-foreground" aria-hidden />
          <p className="text-base font-semibold text-foreground">Todavía no hay grupos</p>
          <p className="max-w-md text-sm text-muted-foreground">
            Creá tu primer grupo para filtrar el Tablero por un equipo completo en vez de
            elegir asesor por asesor.
          </p>
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 size-4" />
            Nuevo grupo
          </Button>
        </Card>
      ) : null}

      {groupList.map((group) => {
        const memberIds = group.employeeIds ?? [];
        return (
          <Card key={group.id} className="flex flex-col gap-4 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {editingId === group.id ? (
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <Input
                    autoFocus
                    value={editingName}
                    maxLength={NAME_MAX_LENGTH}
                    className="max-w-xs"
                    aria-label="Nombre del grupo"
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(group.id);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Guardar nombre"
                    disabled={!editingName.trim() || renameGroup.isLoading}
                    onClick={() => handleRename(group.id)}
                  >
                    <Check className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Cancelar"
                    onClick={() => setEditingId(null)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground">{group.name}</h3>
                  <span className="text-sm text-muted-foreground">
                    {memberIds.length === 1 ? "1 asesor" : `${memberIds.length} asesores`}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Renombrar ${group.name}`}
                  onClick={() => handleStartEditing(group)}
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Eliminar ${group.name}`}
                  className="text-red-600 hover:text-red-700"
                  onClick={() => setGroupToDelete({ id: group.id, name: group.name })}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>

            {/* No `groups` preset here: this tab is where a group's roster is
                defined, so offering other groups as shortcuts would invite
                circular thinking about what a group contains. */}
            <EmployeeAssignment
              assignedEmployees={memberIds}
              availableEmployees={availableEmployees}
              onAddEmployees={(employeeIds) =>
                addEmployees.mutate({ merchantId, id: group.id, employeeIds })
              }
              onRemoveEmployees={(employeeIds) =>
                removeEmployees.mutate({ merchantId, id: group.id, employeeIds })
              }
            />

            {memberIds.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Un grupo sin asesores aparece desactivado en el filtro del Tablero.
              </p>
            ) : null}
          </Card>
        );
      })}

      <AlertDialog
        open={!!groupToDelete}
        onOpenChange={(open) => !open && setGroupToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar grupo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar <strong>{groupToDelete?.name}</strong>? Los asesores
              no se ven afectados, solo se elimina el grupo. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 text-white hover:bg-red-600"
              onClick={() =>
                groupToDelete && deleteGroup.mutate({ merchantId, id: groupToDelete.id })
              }
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
